import { z } from 'zod';
import { AI_DRAFT_KINDS, type AiDraftKind, type Lang, REPLY_CLASS_VALUES } from '../constants';
import { idSchema } from './common';

// ---- what the model must return (server re-validates with these) ----

/** `ref` = 1-based number of the message as listed in the prompt; 0 = not tied to a message. */
export const aiSummaryOutputSchema = z.object({
  bullets: z.array(z.object({ text: z.string().max(400), ref: z.int().min(0) })).max(8),
  warn: z.string().max(300),
});
export type AiSummaryOutput = z.infer<typeof aiSummaryOutputSchema>;

/** The reply classifier's answer: for each message asked about, by its key, whether it waits for an answer. */
export const aiReplyClassOutputSchema = z.object({
  items: z.array(z.object({ key: z.string().max(20), cls: z.enum(REPLY_CLASS_VALUES) })).max(20),
});
export type AiReplyClassOutput = z.infer<typeof aiReplyClassOutputSchema>;

export const aiDraftOutputSchema = z.object({
  subject: z.string().max(200),
  body: z.string().max(6000),
});
export type AiDraftOutput = z.infer<typeof aiDraftOutputSchema>;

/** `refKey` must be one of the candidate keys listed in the prompt ('' = none). */
export const aiDigestOutputSchema = z.object({
  items: z
    .array(z.object({ text: z.string().max(240), meta: z.string().max(160), refKey: z.string().max(12) }))
    .max(7),
});
export type AiDigestOutput = z.infer<typeof aiDigestOutputSchema>;

// ---- request bodies ----

export const aiDraftRequestSchema = z
  .object({
    kind: z.enum(AI_DRAFT_KINDS),
    instruction: z.string().trim().max(1000).default(''),
  })
  .refine((r) => r.kind !== 'custom' || r.instruction.length > 0, {
    message: 'Za osnutek po lastnem navodilu vpiši navodilo.',
  });
export type AiDraftRequest = z.infer<typeof aiDraftRequestSchema>;

export const volunteerEmailSchema = z.object({
  subject: z.string().trim().min(1).max(300),
  body: z.string().trim().min(1).max(20000),
  /** When the text came from an AI draft, link the sent message back to it. */
  draftId: idSchema.optional(),
});
export type VolunteerEmailInput = z.infer<typeof volunteerEmailSchema>;

// ---- API payloads ----

export interface AiSummary {
  id: string;
  volunteerId: string;
  bullets: { text: string; messageId: string | null }[];
  warn: string;
  model: string;
  messageCount: number;
  createdAt: string;
}

export interface AiDraft {
  id: string;
  volunteerId: string;
  kind: AiDraftKind;
  instruction: string;
  subject: string;
  body: string;
  lang: Lang;
  model: string;
  sentMessageId: string | null;
  createdAt: string;
}

export type DigestLink =
  | { kind: 'volunteer'; volunteerId: string }
  | { kind: 'gap'; eventDayId: string; postId: string; fromMin: number; toMin: number }
  | { kind: 'timeline'; eventDayId: string };

export interface AiDigestCounts {
  /** Unstaffed shifts on days that are not yet over. */
  open: number;
  /** People with shifts still waiting to confirm. */
  pending: number;
  /** People with shifts whose current schedule version was never sent. */
  noBriefing: number;
  /** Threads that need a coordinator action (awaiting our reply or stale). */
  needsAction: number;
}

export interface AiDigestItem {
  text: string;
  meta: string;
  /** Severity tone for the dot colour. */
  tone: 'red' | 'amber' | 'blue' | 'neutral';
  link: DigestLink | null;
}

export interface AiDigest {
  id: string;
  counts: AiDigestCounts;
  items: AiDigestItem[];
  model: string;
  createdAt: string;
}
